import { CommonModule } from '@angular/common';
import { Component, inject, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { IonButton, IonContent, IonHeader, IonTitle, IonToggle, IonToolbar, ModalController } from '@ionic/angular/standalone';
import { DateUtil, DisplayService } from '@qid/core';
import { SexyModule } from '@qid/sexy';
import { SuperButtonComponent, SuperInputComponent } from '@qid/super';
import { Subscription } from 'rxjs';
import { IntegrationConfigurationFacade } from '../../../integration-configuration';
import { Share } from '../../model/Share';
import { TimeIntegrationComponent } from '../time-integration/time-integration.component';



@Component({
    selector: 'checkout-modal',
    templateUrl: './checkout-modal.component.html',
    styleUrls: ['./checkout-modal.component.scss'],
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        SexyModule,
        SuperButtonComponent,
        SuperInputComponent,
        TimeIntegrationComponent,
        IonHeader,
        IonToolbar,
        IonButton,
        IonTitle,
        IonContent,
        IonToggle
    ]
})
export class CheckoutModalComponent implements OnInit, OnDestroy {

    @Input() share: Share;
    @Input() editMode: boolean = false;
    // ponytail: await before dismiss so Update keeps modal + super-button loader
    @Input() saveCheckIn?: (checkInTime: Date) => Promise<boolean>;

    checkoutData = {
        checkInDate: new Date().toISOString().split('T')[0],
        checkInTime: DateUtil.getCurrentTime24(),
        checkOutDate: new Date().toISOString().split('T')[0],
        checkOutTime: DateUtil.getCurrentTime24(),
        remark: '',
        submitToTimes: true
    };

    timeConfigReady: boolean = false;
    private subscription: Subscription = new Subscription();
    private integrationConfigurationFacade = inject(IntegrationConfigurationFacade);
    private displayService = inject(DisplayService);
    @ViewChild('timeIntegration') timeIntegration: TimeIntegrationComponent;

    constructor(private modalController: ModalController) { }

    ngOnInit() {
        // Get check-in date from share.createTime or share.checkInData?.checkInTime
        const checkInTime = this.share?.checkInData?.checkInTime || this.share?.createTime;

        if (checkInTime) {
            const checkInDate = new Date(checkInTime);
            this.checkoutData.checkInDate = checkInDate.toISOString().split('T')[0];
            const hours = String(checkInDate.getHours()).padStart(2, '0');
            const minutes = String(checkInDate.getMinutes()).padStart(2, '0');
            this.checkoutData.checkInTime = `${hours}:${minutes}`;
        } else {
            // Fallback to current date/time if no check-in time is available
            this.checkoutData.checkInDate = new Date().toISOString().split('T')[0];
            this.checkoutData.checkInTime = DateUtil.getCurrentTime24();
        }

        // Set default check-out values
        this.checkoutData.checkOutDate = new Date().toISOString().split('T')[0];
        this.checkoutData.checkOutTime = DateUtil.getCurrentTime24();

        this.integrationConfigurationFacade.init();
        // Subscribe to TIME config ready state
        this.subscription.add(
            this.integrationConfigurationFacade.timeConfigReady$.subscribe(timeConfigReady => {
                this.timeConfigReady = timeConfigReady;
            })
        );
    }

    ngOnDestroy() {
        this.subscription.unsubscribe();
    }

    onCancel() {
        this.modalController.dismiss();
    }

    onCheckInDateChange() {
        // Automatically set check-out date to next day if it's not set or is before check-in
        if (this.checkoutData.checkInDate) {
            const checkInDate = new Date(this.checkoutData.checkInDate);
            const checkOutDate = new Date(this.checkoutData.checkOutDate);

            // If check-out date is not set or is before/equal to check-in date, set it to next day
            if (!this.checkoutData.checkOutDate || checkOutDate <= checkInDate) {
                const nextDay = new Date(checkInDate);
                nextDay.setDate(nextDay.getDate() + 1);
                this.checkoutData.checkOutDate = nextDay.toISOString().split('T')[0];
            }
        }
    }

    onSubmit = async () => {
        // For edit mode, only validate check-in fields
        if (this.editMode) {
            if (!this.checkoutData.checkInDate || !this.checkoutData.checkInTime) {
                this.displayService.toast({
                    message: 'Please fill in all required check-in date and time fields'
                });
                return;
            }

            // Create the check-in date object
            let checkInDate = new Date(this.checkoutData.checkInDate);
            checkInDate.setHours(+this.checkoutData.checkInTime.split(':')[0]);
            checkInDate.setMinutes(+this.checkoutData.checkInTime.split(':')[1]);

            if (this.saveCheckIn) {
                const ok = await this.saveCheckIn(checkInDate);
                if (!ok) {
                    return;
                }
            }

            this.modalController.dismiss({
                success: true,
                data: {
                    checkInTime: checkInDate
                }
            });
            return;
        }

        // Validate the form
        if (!this.checkoutData.checkInDate || !this.checkoutData.checkInTime ||
            !this.checkoutData.checkOutDate || !this.checkoutData.checkOutTime) {
            this.displayService.toast({
                message: 'Please fill in all required date and time fields'
            });
            return;
        }

        // Create the check-in date object
        let checkInDate = new Date(this.checkoutData.checkInDate);
        checkInDate.setHours(+this.checkoutData.checkInTime.split(':')[0]);
        checkInDate.setMinutes(+this.checkoutData.checkInTime.split(':')[1]);

        // Create the check-out date object
        let checkOutDate = new Date(this.checkoutData.checkOutDate);
        checkOutDate.setHours(+this.checkoutData.checkOutTime.split(':')[0]);
        checkOutDate.setMinutes(+this.checkoutData.checkOutTime.split(':')[1]);

        // Validate that check-out is after check-in
        if (checkOutDate <= checkInDate) {
            this.displayService.toast({
                message: 'Check-out time must be after check-in time'
            });
            return;
        }

        // Validate TIME fields if submitToTimes is enabled
        if (this.checkoutData.submitToTimes && this.timeIntegration) {
            // Mark all TIME form fields as touched to trigger validation display
            this.timeIntegration.timeForm.markAllAsTouched();

            // Check if TIME form is valid
            if (!this.timeIntegration.timeForm.valid) {
                this.displayService.toast({
                    message: 'Please fill in all required TIME fields: Number of Rooms, Charges, Purpose of Visit, Country, and State'
                });
                return; // Prevent submission if TIME form has errors
            }

            // Check if totals match (age groups vs gender totals)
            if (!this.timeIntegration.totalsMatch) {
                this.displayService.toast({
                    message: 'Age group totals do not match gender totals. Please fix the data mismatch.'
                });
                return; // Prevent submission if totals don't match
            }

            // Submit to TIME integration
            const timeSubmissionResult = await this.timeIntegration.uploadTimesData();

            if (timeSubmissionResult?.data?.Success === "N") {
                throw new Error(`TIME ${timeSubmissionResult?.data?.message}`);
            }

            // Check if TIME submission was successful
            if (!timeSubmissionResult) {
                this.displayService.toast({
                    message: 'Failed to submit TIME data. Please try again.'
                });
                return; // Prevent submission if TIME integration fails
            }
        }

        // Only dismiss modal if all validations pass
        this.modalController.dismiss({
            success: true,
            data: {
                checkInTime: checkInDate,
                checkOutTime: checkOutDate,
                remark: this.checkoutData.remark,
                submitToTimes: this.checkoutData.submitToTimes
            }
        });
    }


}
